import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { type SubtitleTrack, getSubtitleTracks } from '../lib/api';
import { DEFAULT_SUBTITLE_STYLE, type SubtitleStyle, applyAssStyle } from './ass-style';
import { pickDefaultTrack } from './default-track';

/**
 * Renderowanie napisów ASS przez libass.
 *
 * Dlaczego nie natywny element `track`: format ASS niesie style, pozycjonowanie
 * i znaki typograficzne, których przeglądarka nie zna — potrafi wyświetlić
 * wyłącznie VTT, i to bez stylowania. Konwersja ASS→VTT gubiłaby dokładnie to,
 * po co fansuberzy używają ASS-a. libass rysuje na warstwie nad wideo i jest
 * jedyną drogą do wiernego wyniku w przeglądarce.
 *
 * Trzy rzeczy, które muszą tu być zrobione dokładnie tak:
 *
 *  1. **Import dynamiczny.** Worker i `.wasm` to razem prawie 3 MB. W initial
 *     bundle podniosłyby czas do pierwszego obrazu dla każdego, także dla widza,
 *     który napisów nie włączy.
 *  2. **`dispose()` przy odmontowaniu.** libass trzyma workera i płótno poza
 *     drzewem Reacta, więc bez tego każda zmiana odcinka zostawiałaby żywego
 *     workera zjadającego CPU.
 *  3. **Czcionka zapasowa.** Kroje z ASS-a rzadko istnieją w przeglądarce, a bez
 *     zapasu libass rysuje puste prostokąty — przy polskich napisach zniknęłyby
 *     najpierw znaki diakrytyczne.
 *
 * Styl widza (rozmiar, obrys, krój, margines) wchodzi przez **przepisanie treści
 * napisów**, nie przez opcję renderera: libass takiego API nie ma, a skalowanie
 * płótna przez CSS rozjechałoby pozycjonowanie. Szczegóły w `ass-style.ts`.
 * Dlatego karmimy instancję `subContent`, a nie `subUrl` — treść musi przejść
 * przez nasze ręce.
 */

/** Zasoby układa `scripts/copy-libass-assets.mjs` przed dev i build. */
const WORKER_URL = '/libass/subtitles-octopus-worker.js';
const LEGACY_WORKER_URL = '/libass/subtitles-octopus-worker-legacy.js';
const FALLBACK_FONT = '/libass/default.woff2';

/**
 * Dopełnienie zapasowego kroju o Europę Środkową.
 *
 * Podzbiory fontsource są rozłączne: `default.woff2` to `latin` (podstawowa łacinka
 * i `ó`), a znaki `ą ć ę ł ń ś ż` leżą dopiero w `latin-ext`. Sam `fallbackFont`
 * przyjmuje **jeden** plik, więc drugi podajemy jako czcionkę dodatkową — obie
 * deklarują rodzinę „Noto Sans", więc libass składa z nich jedno pokrycie.
 *
 * Wcześniej `default.woff2` był SAMYM `latin-ext`, czyli krojem bez liter, którymi
 * pisze się cokolwiek. Napisy szły wtedy bez polskich znaków.
 */
const EXTRA_FONTS = ['/libass/latin-ext.woff2'];

/**
 * Poniżej tej liczby klatek napisy z animowanym pozycjonowaniem szarpią, powyżej
 * rośnie zużycie CPU bez widocznego zysku. Dwadzieścia cztery to zarazem
 * typowa liczba klatek anime.
 */
const TARGET_FPS = 24;

/**
 * Styl trzymamy w `localStorage`, **nie na koncie**.
 *
 * To preferencja urządzenia, nie osoby: ten sam widz na telewizorze z trzech
 * metrów chce większego tekstu niż na laptopie z pięćdziesięciu centymetrów.
 * Zapis na koncie synchronizowałby ustawienie, które ma prawo się różnić, i przy
 * każdej zmianie ekranu wymagałby korekty w drugą stronę.
 */
const STORAGE_KEY = 'anisync.subtitleStyle';

export interface SubtitlesState {
  tracks: SubtitleTrack[];
  /** Identyfikator wybranej ścieżki albo `null`, gdy napisy są wyłączone. */
  activeId: string | null;
  /** Przesunięcie w sekundach; dodatnie opóźnia napisy. */
  offsetSec: number;
  /**
   * Polska ścieżka istnieje, ale świadomie jej nie włączyliśmy.
   *
   * Materiał z polskich źródeł bywa z napisami wtopionymi w obraz. Interfejs mówi
   * o tym jednym zdaniem — inaczej wyłączone napisy przy dostępnej ścieżce wyglądają
   * na awarię, a nie na decyzję.
   */
  suppressedHardsub: boolean;
  /** Renderer wstaje asynchronicznie — to trwa u widza chwilę. */
  loading: boolean;
  error: string | null;
  select(trackId: string | null): void;
  shiftBy(deltaSec: number): void;
  /** Styl obowiązujący. Zmiany wchodzą w żywą instancję, bez przebudowy workera. */
  style: SubtitleStyle;
  setStyle(patch: Partial<SubtitleStyle>): void;
  resetStyle(): void;
  /**
   * Wczytuje listę ścieżek od nowa.
   *
   * Potrzebne, bo ścieżka może powstać **w trakcie seansu**: widz pobiera napisy
   * z animesuba i chce je natychmiast zobaczyć. Bez tego lista pozostawałaby ta, którą
   * odtwarzacz zobaczył przy starcie materiału.
   */
  reload(): void;
}

export function useSubtitles({
  videoRef,
  mediaFileId,
  ready,
}: {
  videoRef: RefObject<HTMLVideoElement | null>;
  mediaFileId: string;
  /** Dopóki materiał nie jest gotowy, elementu wideo nie ma czym karmić. */
  ready: boolean;
}): SubtitlesState {
  const [tracks, setTracks] = useState<SubtitleTrack[]>([]);
  const [style, setStyleState] = useState<SubtitleStyle>(readStoredStyle);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [offsetSec, setOffsetSec] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suppressedHardsub, setSuppressedHardsub] = useState(false);
  /** Licznik wymuszający ponowne wczytanie listy ścieżek. */
  const [reloadToken, setReloadToken] = useState(0);

  /**
   * Instancja renderera żyje POZA stanem Reacta.
   *
   * Trzymanie jej w `useState` powodowałoby przerysowanie przy każdym utworzeniu,
   * a sama instancja nie jest wartością do wyświetlenia — jest zasobem
   * o cyklu życia. Ref oddaje to wiernie.
   */
  const octopusRef = useRef<{
    dispose(): void;
    setTrack(content: string): void;
    timeOffset: number;
  } | null>(null);

  /**
   * Surowa treść napisów, obok instancji renderera.
   *
   * Zmiana stylu przepisuje plik od nowa, więc potrzebujemy oryginału — bez tego
   * druga zmiana skalowałaby to, co już zeskalowane, i rozmiar rósłby wykładniczo
   * przy każdym ruchu suwaka.
   */
  const sourceRef = useRef<string | null>(null);

  /**
   * Styl w refie, obok stanu — z tego samego powodu co przesunięcie: efekt
   * tworzący renderer musi znać wartość początkową, ale nie może od niej zależeć.
   */
  const styleRef = useRef<SubtitleStyle>(style);

  /**
   * Bieżące przesunięcie w refie, obok stanu.
   *
   * Efekt tworzący renderer musi znać wartość początkową, ale NIE MOŻE od niej
   * zależeć — inaczej każde naciśnięcie `[` przebudowywałoby workera i napisy
   * mrugałyby przy każdej korekcie. Ref daje dostęp bez zależności, i to jest
   * uczciwszy zapis intencji niż wyciszenie reguły o zależnościach.
   */
  const offsetRef = useRef(0);

  // Lista ścieżek: jedno żądanie na materiał.
  useEffect(() => {
    setTracks([]);
    setActiveId(null);
    setError(null);
    setSuppressedHardsub(false);
    /**
     * `reloadToken` jest **sygnałem**, nie wartością: rośnie, gdy ktoś dograł napisy
     * w trakcie seansu. Odczyt stoi tu jawnie, bo bez użycia w ciele efektu wygląda
     * jak zbędna zależność — a jest jedynym powodem, dla którego lista odświeża się
     * bez zmiany materiału.
     */
    void reloadToken;
    if (!ready) return;

    let cancelled = false;
    getSubtitleTracks(mediaFileId)
      .then((data) => {
        if (cancelled) return;
        setTracks(data.tracks);
        /**
         * Co włącza się samo — decyduje `pickDefaultTrack`.
         *
         * To aplikacja do oglądania z polskimi napisami, więc wymaganie kliknięcia
         * przy każdym odcinku byłoby uporczywe. Ale materiał z polskich źródeł bywa
         * z napisami **wtopionymi w obraz**, a druga warstwa na wierzchu daje dwa
         * teksty jeden nad drugim — dlatego plik dostarczony obok materiału przez to
         * samo źródło zostaje wyłączony, dopóki widz go nie wybierze.
         */
        const choice = pickDefaultTrack(data.tracks);
        setActiveId(choice.trackId);
        setSuppressedHardsub(choice.suppressedHardsub);
      })
      .catch(() => {
        // Brak napisów nie jest awarią odtwarzania, więc nie krzyczymy.
        if (!cancelled) setTracks([]);
      });

    return () => {
      cancelled = true;
    };
  }, [mediaFileId, ready, reloadToken]);

  // Renderer: tworzony przy wyborze ścieżki, niszczony przy zmianie i wyjściu.
  useEffect(() => {
    const video = videoRef.current;
    const track = tracks.find((t) => t.id === activeId);

    function teardown(): void {
      octopusRef.current?.dispose();
      octopusRef.current = null;
    }

    if (video === null || track === undefined) {
      teardown();
      return;
    }

    // VTT i SRT umie sama przeglądarka; libass jest potrzebny dla ASS. Na razie
    // wszystkie nasze ścieżki to ASS, ale kierowanie po formacie zostaje, żeby
    // dodanie VTT nie wymagało zmiany tej decyzji w dwóch miejscach.
    if (track.format !== 'ass') {
      teardown();
      return;
    }

    let disposed = false;
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        /**
         * Treść pobieramy sami, zamiast dawać rendererowi adres.
         *
         * Bez tego nie da się zastosować stylu widza: libass dostałby plik
         * z serwera i narysował go dokładnie tak, jak jest. Koszt to jedno żądanie
         * na ścieżkę — plik ASS odcinka waży kilkadziesiąt kilobajtów.
         */
        const [{ default: SubtitlesOctopus }, source] = await Promise.all([
          import('libass-wasm'),
          fetch(track.url).then((res) => {
            if (!res.ok) throw new Error(`napisy: ${res.status}`);
            return res.text();
          }),
        ]);
        if (disposed) return;

        sourceRef.current = source;
        teardown();
        const instance = new SubtitlesOctopus({
          video,
          subContent: applyAssStyle(source, styleRef.current),
          workerUrl: WORKER_URL,
          legacyWorkerUrl: LEGACY_WORKER_URL,
          fallbackFont: FALLBACK_FONT,
          fonts: EXTRA_FONTS,
          targetFps: TARGET_FPS,
          onReady: () => {
            if (!disposed) setLoading(false);
          },
          onError: () => {
            if (!disposed) {
              setError('Nie udało się wyrenderować napisów');
              setLoading(false);
            }
          },
        });
        instance.timeOffset = offsetRef.current;
        octopusRef.current = instance;
      } catch {
        if (!disposed) {
          setError('Nie udało się wczytać silnika napisów');
          setLoading(false);
        }
      }
    })();

    return () => {
      disposed = true;
      teardown();
    };
  }, [activeId, tracks, videoRef]);

  /**
   * Zmiana stylu idzie przez `setTrack`, nie przez przebudowę instancji.
   *
   * Worker libassa wstaje kilkaset milisekund i pobiera trzy megabajty zasobów —
   * przy suwaku rozmiaru oznaczałoby to migające napisy i zajęty procesor na
   * każdym kroku suwaka.
   */
  useEffect(() => {
    styleRef.current = style;
    const source = sourceRef.current;
    if (octopusRef.current === null || source === null) return;
    octopusRef.current.setTrack(applyAssStyle(source, style));
  }, [style]);

  // Przesunięcie stosujemy do ŻYWEJ instancji — bez przebudowy renderera.
  useEffect(() => {
    offsetRef.current = offsetSec;
    if (octopusRef.current !== null) octopusRef.current.timeOffset = offsetSec;
  }, [offsetSec]);

  const setStyle = useCallback((patch: Partial<SubtitleStyle>) => {
    setStyleState((current) => {
      const next = { ...current, ...patch };
      persistStyle(next);
      return next;
    });
  }, []);

  const resetStyle = useCallback(() => {
    persistStyle(DEFAULT_SUBTITLE_STYLE);
    setStyleState(DEFAULT_SUBTITLE_STYLE);
  }, []);

  const select = useCallback((trackId: string | null) => {
    setActiveId(trackId);
    // Przesunięcie należy do ścieżki, nie do widza: zmieniając napisy zaczynamy
    // od zera, bo nowe wydanie ma inne cięcie.
    setOffsetSec(0);
  }, []);

  const shiftBy = useCallback((deltaSec: number) => {
    // Ograniczenie do ±30 s: większe wartości nie są przesunięciem, a objawem
    // napisów do innego wydania — tam potrzebne jest wyrównanie, nie suwak.
    setOffsetSec((current) =>
      Math.max(-30, Math.min(30, Math.round((current + deltaSec) * 4) / 4)),
    );
  }, []);

  return {
    tracks,
    activeId,
    offsetSec,
    loading,
    error,
    suppressedHardsub,
    select,
    shiftBy,
    style,
    setStyle,
    resetStyle,
    reload: () => setReloadToken((value) => value + 1),
  };
}

/**
 * Odczyt zapisanego stylu.
 *
 * Nieznane i uszkodzone wartości **milcząco wracają do domyślnych**: zapis
 * w przeglądarce pochodzi z poprzedniej wersji aplikacji albo z ręcznej edycji,
 * a napisy w domyślnym rozmiarze są lepsze od wyjątku w odtwarzaczu.
 */
function readStoredStyle(): SubtitleStyle {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return DEFAULT_SUBTITLE_STYLE;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return DEFAULT_SUBTITLE_STYLE;
    const record = parsed as Record<string, unknown>;
    return {
      scale: numberOr(record.scale, DEFAULT_SUBTITLE_STYLE.scale),
      outline: numberOr(record.outline, DEFAULT_SUBTITLE_STYLE.outline),
      fontFamily: typeof record.fontFamily === 'string' ? record.fontFamily : null,
      marginBottom: numberOr(record.marginBottom, DEFAULT_SUBTITLE_STYLE.marginBottom),
    };
  } catch {
    return DEFAULT_SUBTITLE_STYLE;
  }
}

function persistStyle(style: SubtitleStyle): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(style));
  } catch {
    // Tryb prywatny i wyłączony magazyn nie mogą zabrać możliwości zmiany stylu —
    // ustawienie po prostu nie przeżyje odświeżenia karty.
  }
}

function numberOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}
